import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Video as VideoIcon, Play, Loader2, AlertCircle, X, ExternalLink } from 'lucide-react';
import api from '../lib/api';

interface Video {
  video_id: string;
  product: string;
  market: string;
  language: string;
  duration: string;
  status: string;
  url: string | null;
  thumbnail: string | null;
}

interface Product {
  id?: string;
  product_id?: string;
  title: string;
}

export default function Videos() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [market, setMarket] = useState('DE');
  const [count, setCount] = useState(1);

  const { data: videos, isLoading, error } = useQuery({
    queryKey: ['videos'],
    queryFn: async () => {
      const response = await api.get<Video[]>('/videos');
      return response.data;
    },
  });

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await api.get<Product[]>('/products');
      return response.data;
    },
  });

  const generateMutation = useMutation({
    mutationFn: async (payload: { product_id: string; market: string; count: number }) => {
      const response = await api.post('/videos', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      setIsModalOpen(false);
      setSelectedProductId('');
    },
  });

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProductId) return;
    generateMutation.mutate({
      product_id: selectedProductId,
      market,
      count,
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="text-gray-400">Loading videos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Videos</h1>
          <p className="text-gray-400 mt-1">AI-generated TikTok/Reels videos</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="btn-primary flex items-center"
        >
          <VideoIcon className="h-5 w-5 mr-2" />
          Generate Video
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 p-4 rounded-lg flex items-center text-red-500">
          <AlertCircle className="h-5 w-5 mr-3" />
          <p>Failed to load videos. Please check if the API is running.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(videos) && videos.length > 0 ? (
          videos.map((video) => (
            <div key={video.video_id} className="card">
              <div className="relative aspect-[9/16] bg-dark-900 rounded-lg overflow-hidden mb-4">
                {video.thumbnail ? (
                  <img 
                    src={video.thumbnail} 
                    alt={video.product}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-600">
                    <VideoIcon className="h-12 w-12 mb-2" />
                    <p className="text-sm">No Preview</p>
                  </div>
                )}
                
                {video.status === 'completed' && video.url && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 group">
                    <a 
                      href={video.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-4 bg-white/20 rounded-full hover:bg-white/30 transition-all"
                    >
                      <Play className="h-8 w-8 text-white" />
                    </a>
                  </div>
                )}

                {video.status === 'rendering' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 text-primary-500 animate-spin mx-auto mb-2" />
                      <p className="text-white text-sm font-medium">Rendering...</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="font-medium text-white line-clamp-1">{video.product}</h3>
                <div className="flex items-center justify-between mt-2 text-sm text-gray-400">
                  <span>{video.market} • {video.language}</span>
                  <span>{video.duration}</span>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className={`
                    px-2 py-1 rounded-full text-xs font-medium
                    ${video.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}
                  `}>
                    {video.status}
                  </span>
                  {video.url && (
                    <a href={video.url} target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full py-20 text-center card">
            <VideoIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No videos generated yet.</p>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="mt-4 text-primary-500 hover:text-primary-400 font-medium"
            >
              Start your first generation →
            </button>
          </div>
        )}
      </div>

      {/* Generate Video Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Generate Video</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Select Product</label>
                <select
                  required
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary-500"
                >
                  <option value="">-- Choose a product --</option>
                  {Array.isArray(products) && products.map((p) => (
                    <option key={p.product_id || p.id} value={p.product_id || p.id}>{p.title}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Market</label>
                  <select
                    value={market}
                    onChange={(e) => setMarket(e.target.value)}
                    className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary-500"
                  >
                    <option value="DE">Germany (DE)</option>
                    <option value="AE">UAE (AE)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Batch Size</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={count}
                    onChange={(e) => setCount(parseInt(e.target.value))}
                    className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="bg-primary-500/10 border border-primary-500/20 p-4 rounded-lg">
                <p className="text-xs text-primary-200 leading-relaxed">
                  <strong>AI Workflow:</strong> Our Content Agent will analyze the product, generate a high-converting script using DeepSeek, and render a 15-30s TikTok video using our Remotion engine.
                </p>
              </div>

              <div className="flex justify-end space-x-3 mt-8">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white font-medium text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={generateMutation.isPending || !selectedProductId}
                  className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generateMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Decomposing Goal...
                    </>
                  ) : (
                    'Generate Now'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
